import { Link, useParams } from "react-router-dom";
import NavigationBar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import ReadingProgress from "@/pages/article/components/ReadingProgress";
import ArticleBody from "@/pages/article/components/ArticleBody";
import ArticleSidebar from "@/pages/article/components/ArticleSidebar";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { getArticle, getRelatedArticles, siteInfo } from "@/utils/content";

export default function ArticlePage() {
  const { id } = useParams();
  const article = getArticle(id);

  const toc = article
    ? article.content
        .map((block, index) => ({ block, index }))
        .filter((item) => item.block.type === "h2")
        .map((item) => ({ id: `section-${item.index}`, text: item.block.text }))
    : [];

  const related = article ? getRelatedArticles(article, 3) : [];
  const index = article ? articles.findIndex((item) => item.id === article.id) : -1;
  const prev = index > 0 ? articles[index - 1] : null;
  const next = index >= 0 && index < articles.length - 1 ? articles[index + 1] : null;

  useSeo(
    article
      ? {
          title: `${article.title} | 511爆料`,
          description: article.summary,
          keywords: `511爆料,${article.categoryName},${article.tags.join(",")}`,
          canonical: `/article/${article.id}`,
          type: "article",
          image: article.cover,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.summary,
              image: article.cover,
              datePublished: article.date,
              dateModified: article.date,
              inLanguage: "zh-CN",
              articleSection: article.categoryName,
              keywords: article.tags.join(","),
              author: { "@type": "Person", name: article.author },
              publisher: {
                "@type": "Organization",
                name: "511爆料",
                url: siteInfo.baseUrl,
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${siteInfo.baseUrl}/article/${article.id}`,
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.baseUrl },
                { "@type": "ListItem", position: 2, name: article.categoryName, item: `${siteInfo.baseUrl}/category/${article.category}` },
                { "@type": "ListItem", position: 3, name: article.title, item: `${siteInfo.baseUrl}/article/${article.id}` },
              ],
            },
          ],
        }
      : {
          title: "内容不存在 | 511爆料",
          description: "你访问的爆料内容可能已被调整或链接有误，可以返回首页重新选择分类。",
          canonical: `/article/${id ?? ""}`,
        },
  );

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <NavigationBar />
        <main className="flex flex-1 items-center justify-center px-4 py-32">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
              <i className="ri-emotion-sad-line" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这条爆料找不到了</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              它可能被调整过，或者链接本身有误。你可以回到首页，从分类导航重新选择感兴趣的方向。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to="/category/all"
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                查看全部爆料
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <ReadingProgress />
      <NavigationBar />
      <main className="flex-1">
        <PageHeader
          eyebrow={article.categoryName}
          title={article.title}
          description={article.summary}
          crumbs={[
            { label: "分类导航", to: "/category/all" },
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <section className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 md:px-8 md:py-14 lg:flex-row">
          <div className="min-w-0 flex-1">
            <ArticleBody article={article} />

            <nav aria-label="相邻内容" className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/article/${prev.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-arrow-left-line" />
                    上一条
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <div className="hidden sm:block" />
              )}
              {next && (
                <Link
                  to={`/article/${next.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-5 text-right transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                    下一条
                    <i className="ri-arrow-right-line" />
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                    {next.title}
                  </span>
                </Link>
              )}
            </nav>
          </div>

          <ArticleSidebar toc={toc} related={related} />
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}